import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '../theme';

/**
 * FancyDropdown
 * - Props:
 *   - options: string[] (display labels)
 *   - value: string (controlled, typically lowercased)
 *   - onChange: (nextValueLower: string) => void
 *   - placeholder?: string
 *   - disabled?: boolean
 *   - allowSearch?: boolean (default true)
 *   - toLowerValue?: boolean (default true) -> emitted values are lowercased
 */
export default function FancyDropdown({ options = [], value, onChange, placeholder = 'Alege', disabled = false, allowSearch = true, toLowerValue = true }) {
  const { tokens } = useTheme();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);

  const normalizedOptions = useMemo(() => {
    return options.map(label => ({
      label,
      value: toLowerValue ? String(label).toLowerCase() : String(label)
    }));
  }, [options, toLowerValue]);

  const selected = useMemo(() => normalizedOptions.find(o => o.value === (value ?? '')) || null, [normalizedOptions, value]);
  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return normalizedOptions;
    return normalizedOptions.filter(o => o.label.toLowerCase().includes(q));
  }, [normalizedOptions, query]);

  useEffect(() => {
    function onDocClick(e) {
      if (!rootRef.current) return;
      if (rootRef.current.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
    };
  }, []);

  const toggle = () => { if (!disabled) setOpen(o => !o); };
  const choose = (opt) => {
    if (disabled) return;
    onChange && onChange(opt.value);
    setOpen(false);
  };

  // Inline variables for theme-aware button appearance
  const btnStyle = {
    background: tokens.panel,
    color: tokens.text,
    border: `1px solid ${tokens.border}`,
  };

  return (
    <div className="fancy-select" ref={rootRef}>
      <button type="button" className="fancy-select__button" style={btnStyle} onClick={toggle} disabled={disabled} aria-expanded={open}>
        <span>{selected ? selected.label : placeholder}</span>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity: disabled ? 0.45 : 0.9 }}>
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <div className="fancy-select__panel">
          {allowSearch && (
            <div className="fancy-select__search">
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Caută..." />
            </div>
          )}
          <div role="listbox" aria-activedescendant={selected ? `opt-${selected.value}` : undefined} tabIndex={0}>
            {filteredOptions.length === 0 ? (
              <div className="fancy-select__option" aria-disabled="true" style={{ opacity: 0.6, cursor: 'default' }}>Niciun rezultat</div>
            ) : (
              filteredOptions.map(opt => (
                <div key={opt.value} id={`opt-${opt.value}`} role="option" aria-selected={selected && selected.value === opt.value ? 'true' : 'false'} className="fancy-select__option" onClick={() => choose(opt)}>
                  <span>{opt.label}</span>
                  {selected && selected.value === opt.value && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
